import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Watch, Copy, ExternalLink, Activity, CheckCircle2, XCircle, RefreshCw } from "lucide-react";

export default function Wearables() {
  const [data, setData] = useState({ whoop: null, stubs: [] });
  const [params, setParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const load = async () => {
    const r = await api.get("/wearables/providers");
    setData(r.data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("whoop") === "connected") {
      toast.success("WHOOP connected. Live recovery data flowing in.");
      setParams({});
      load();
    }
  }, [params, setParams]);

  const connectWhoop = async () => {
    try {
      const r = await api.get("/wearables/whoop/authorize");
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error("Couldn't start WHOOP auth");
    }
  };

  const disconnectWhoop = async () => {
    await api.post("/wearables/whoop/disconnect");
    toast("WHOOP disconnected");
    load();
  };

  const runDiagnostic = async () => {
    setDiagLoading(true);
    try {
      const r = await api.get("/wearables/whoop/diagnostics");
      setDiag(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostic failed");
    } finally {
      setDiagLoading(false);
    }
  };

  const redirectUri = `${window.location.origin}/api/wearables/whoop/callback`;
  const copyUri = async () => {
    try {
      await navigator.clipboard.writeText(redirectUri);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const connected = data.whoop?.status === "connected";

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14 space-y-8" data-testid="wearables-page">
      <div>
        <div className="eyebrow">CONNECT</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase mt-2 leading-none">Plug in the data.</h1>
        <p className="text-neutral-600 mt-3 max-w-xl">Real biometrics only. FLYN, stakes, and recommendations all read live from your wearable.</p>
      </div>

      {/* WHOOP card */}
      <div className="panel p-7 rounded-2xl flex items-center justify-between flex-wrap gap-4" data-testid="whoop-card">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-obsidian text-lime flex items-center justify-center">
            <Watch className="w-8 h-8" />
          </div>
          <div>
            <div className="font-display text-3xl leading-none">WHOOP</div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 mt-1">
              {connected ? <span className="lime-dot text-obsidian">CONNECTED · LIVE</span> : "RECOVERY · HRV · SLEEP · STRAIN"}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={runDiagnostic} disabled={diagLoading} className="btn-outline-ink" data-testid="test-connection">
            <Activity className="w-4 h-4" /> {diagLoading ? "Testing…" : connected ? "Test connection" : "Test config"}
          </button>
          {connected ? (
            <button onClick={disconnectWhoop} className="btn-outline-ink" data-testid="disconnect-whoop">Disconnect</button>
          ) : (
            <button onClick={connectWhoop} className="btn-lime" data-testid="connect-whoop">Connect WHOOP</button>
          )}
        </div>
      </div>

      {/* Diagnostic drawer */}
      {diag && (
        <div className="panel p-6 rounded-2xl" data-testid="whoop-diagnostic">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow">DIAGNOSTIC · {new Date().toLocaleTimeString()}</div>
            <button onClick={runDiagnostic} className="text-neutral-500 hover:text-black" data-testid="diag-refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <DiagRow label="Client ID configured" ok={diag.config?.client_id_set}>
              <code className="text-xs">{diag.config?.client_id}</code>
            </DiagRow>
            <DiagRow label="Client secret configured" ok={diag.config?.client_secret_set} />
            <DiagRow label="WHOOP reachable" ok={diag.whoop_reachable?.ok}>
              <span className="text-xs text-neutral-500">HTTP {diag.whoop_reachable?.status || "—"}</span>
            </DiagRow>
            <DiagRow label="Has access token" ok={diag.token?.has_token}>
              {diag.token?.has_token && (
                <span className="text-xs text-neutral-500">
                  {diag.token.expired ? "expired · will refresh" : "valid"} · scope: {diag.token.scope || "n/a"}
                </span>
              )}
            </DiagRow>
            <DiagRow label="Latest snapshot in DB" ok={!!diag.latest_snapshot}>
              {diag.latest_snapshot && (
                <span className="text-xs text-neutral-500">
                  {diag.latest_snapshot.date} · source: {diag.latest_snapshot.source} · recovery {diag.latest_snapshot.recovery_pct ?? "—"}%
                </span>
              )}
            </DiagRow>
            <DiagRow label="Fresh WHOOP fetch" ok={diag.fresh_fetch?.ok}>
              {diag.fresh_fetch ? (
                diag.fresh_fetch.ok ? (
                  <span className="text-xs text-neutral-500">
                    recovery {diag.fresh_fetch.recovery_pct}% · HRV {diag.fresh_fetch.hrv_ms}ms · sleep {diag.fresh_fetch.sleep_score}
                  </span>
                ) : (
                  <span className="text-xs text-red-600">{diag.fresh_fetch.reason}</span>
                )
              ) : (
                <span className="text-xs text-neutral-500">Skipped — connect WHOOP first</span>
              )}
            </DiagRow>
          </div>

          <div className="mt-6 pt-6 border-t border-neutral-200">
            <div className="eyebrow mb-2">REDIRECT URIS TO REGISTER IN WHOOP APP</div>
            <div className="space-y-2">
              <RedirectRow uri={diag.config?.redirect_uri_wearable_connect} label="Wearable connect" />
              <RedirectRow uri={diag.config?.redirect_uri_whoop_login} label="Sign-in with WHOOP" />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-neutral-200">
            <div className="eyebrow mb-2">RAW AUTHORIZE URL · CLICK TO TEST</div>
            <a
              href={diag.preview_authorize_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-xs break-all underline text-obsidian hover:text-lime-dark"
              data-testid="diag-authorize-url"
            >
              {diag.preview_authorize_url}
            </a>
            <div className="text-[11px] font-mono text-neutral-500 mt-2 uppercase tracking-widest">
              If this dead-ends at a WHOOP error page, the redirect URI is not registered in your WHOOP app.
            </div>
          </div>
        </div>
      )}

      {/* Redirect URI quick helper — only when not connected */}
      {!connected && (
        <div className="panel p-6 rounded-2xl border-l-4 border-l-lime" data-testid="whoop-redirect-helper">
          <div className="eyebrow mb-2">FIRST TIME? · REGISTER THIS REDIRECT URI IN YOUR WHOOP APP</div>
          <p className="text-sm text-neutral-700 mb-4">
            WHOOP will reject the OAuth if this exact URL is not registered in your developer app.
            Add it at{" "}
            <a
              href="https://developer.whoop.com/apps"
              target="_blank"
              rel="noopener noreferrer"
              className="underline inline-flex items-center gap-1"
              data-testid="whoop-portal-link"
            >
              developer.whoop.com/apps <ExternalLink className="w-3 h-3" />
            </a>{" "}
            → your app → Redirect URIs.
          </p>
          <div className="flex items-center gap-2 bg-neutral-100 rounded-xl px-4 py-3 font-mono text-xs md:text-sm overflow-x-auto">
            <span className="text-neutral-500 shrink-0">↳</span>
            <span data-testid="whoop-redirect-uri" className="text-obsidian">{redirectUri}</span>
            <button
              onClick={copyUri}
              className="ml-auto shrink-0 rounded-md border border-neutral-300 hover:border-black px-3 py-1 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1"
              data-testid="copy-redirect-uri"
            >
              <Copy className="w-3 h-3" /> {copied ? "COPIED" : "COPY"}
            </button>
          </div>
          <div className="mt-3 text-[11px] font-mono text-neutral-500 tracking-widest uppercase">
            IF YOU'RE ON PRODUCTION (itsfslab.com), ADD BOTH THE PRODUCTION AND PREVIEW URIs.
          </div>
        </div>
      )}

      {/* Stub cards */}
      <div>
        <div className="eyebrow mb-3">COMING SOON · PHASE 2</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {data.stubs.map((s) => (
            <div key={s.id} className="border border-neutral-200 rounded-2xl p-5 flex items-center justify-between opacity-70" data-testid={`stub-${s.id}`}>
              <div>
                <div className="font-display text-2xl leading-none">{s.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                  {s.platform === "ios" ? "iOS ONLY" : s.platform === "android" ? "ANDROID" : "COMING SOON"}
                </div>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">SOON</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DiagRow({ label, ok, children }) {
  return (
    <div className="border border-neutral-200 rounded-xl p-3 flex items-start gap-3">
      {ok === true ? (
        <CheckCircle2 className="w-5 h-5 text-lime-dark shrink-0" />
      ) : ok === false ? (
        <XCircle className="w-5 h-5 text-red-500 shrink-0" />
      ) : (
        <div className="w-5 h-5 rounded-full border-2 border-neutral-300 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</div>
        {children && <div className="mt-1 break-all">{children}</div>}
      </div>
    </div>
  );
}

function RedirectRow({ uri, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(uri || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <div className="bg-neutral-100 rounded-xl px-3 py-2 flex items-center gap-2 font-mono text-xs">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">{label}:</span>
      <span className="flex-1 truncate">{uri}</span>
      <button
        onClick={copy}
        className="rounded-md border border-neutral-300 hover:border-black px-2 py-0.5 text-[10px] uppercase tracking-widest shrink-0"
        data-testid={`copy-${label.toLowerCase().replace(/\s+/g,'-')}`}
      >
        {copied ? "COPIED" : "COPY"}
      </button>
    </div>
  );
}
